import { useEffect, useMemo, useRef, useState } from 'react';
import { HostSection } from '../components/host/HostLayout';
import { hostListProperties } from '../api/host';
import * as api from '../services/hostMessagesApi';

const tabs = [
  { label: 'All', value: '' },
  { label: 'Unread', value: 'unread' },
  { label: 'Flagged', value: 'flagged' },
];

const formatDate = (ts) => {
  if (!ts) return 'N/A';
  const d = new Date(ts);
  return d.toLocaleString();
};

const MessageBubble = ({ msg, isOwn }) => {
  const base = 'px-4 py-2 rounded-2xl text-sm max-w-xl';
  if (msg.is_deleted) {
    return (
      <div className={`text-xs text-slate-500 italic ${isOwn ? 'text-right ml-auto' : 'text-left mr-auto'}`}>
        This message was removed by admin.
      </div>
    );
  }
  return (
    <div className={`space-y-1 ${isOwn ? 'text-right' : 'text-left'}`}>
      <div
        className={`${base} ${isOwn ? 'bg-emerald-600 text-white ml-auto' : 'bg-slate-100 text-slate-900 mr-auto'}`}
      >
        {msg.body}
      </div>
      <div className="text-[11px] text-slate-500">{formatDate(msg.created_at)}</div>
    </div>
  );
};

const QuickLink = ({ href, label }) => {
  if (!href) return null;
  return (
    <a
      href={href}
      className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
      target="_blank"
      rel="noreferrer"
    >
      {label}
    </a>
  );
};

const ConversationListItem = ({ convo, active, onClick }) => {
  const name = convo.guest_name || 'Guest';
  const property = convo.property_title || 'Property';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-xl border px-3 py-3 transition ${
        active ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:border-emerald-200 hover:bg-slate-50'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-900">{name}</div>
        {convo.host_unread_count > 0 && (
          <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[11px] font-semibold text-white">
            {convo.host_unread_count}
          </span>
        )}
      </div>
      <div className="text-xs text-slate-500">{property}</div>
      <div className="mt-1 line-clamp-2 text-xs text-slate-600">{convo.last_message_preview || 'No messages yet'}</div>
      <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
        <span>{convo.booking_code}</span>
        <span>{convo.last_message_at ? new Date(convo.last_message_at).toLocaleDateString() : 'N/A'}</span>
      </div>
      {convo.admin_flagged && (
        <div className="mt-1 text-[11px] font-semibold text-amber-700">Flagged for admin review</div>
      )}
    </button>
  );
};

const HostMessages = ({ accessToken }) => {
  const [filters, setFilters] = useState({ status: '', q: '', property_id: '' });
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState({ messages: [], booking: null, contact: null, conversation: null });
  const [loadingList, setLoadingList] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [properties, setProperties] = useState([]);
  const [flagging, setFlagging] = useState(false);
  const textareaRef = useRef(null);

  const loadProperties = async () => {
    try {
      const res = await hostListProperties(accessToken, { limit: 50 });
      setProperties(res.items || []);
    } catch (_) {
      // ignore
    }
  };

  const loadConversations = async (silent = false) => {
    if (!accessToken) return;
    if (!silent) setLoadingList(true);
    setError('');
    try {
      const res = await api.listConversations(accessToken, {
        status: filters.status,
        q: filters.q,
        property_id: filters.property_id,
        limit: 50,
      });
      setConversations(res.items || res.data || []);
      if (!selectedId && (res.items || res.data || []).length) {
        setSelectedId((res.items || res.data)[0].id);
      }
    } catch (err) {
      setError(err.message || 'Failed to load conversations');
    } finally {
      if (!silent) setLoadingList(false);
    }
  };

  const loadMessages = async (id, silent = false) => {
    if (!id || !accessToken) return;
    if (!silent) setLoadingMessages(true);
    setError('');
    try {
      const res = await api.getMessages(id, accessToken, { limit: 100 });
      setThread({
        messages: res.messages || [],
        booking: res.booking,
        contact: res.contact,
        conversation: res.conversation,
      });
      await api.markRead(id, accessToken).catch(() => {});
    } catch (err) {
      setError(err.message || 'Failed to load messages');
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  };

  useEffect(() => {
    loadProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.property_id]);

  useEffect(() => {
    const t = setTimeout(() => loadConversations(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.q]);

  useEffect(() => {
    if (selectedId) {
      loadMessages(selectedId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const onSend = async () => {
    if (!selectedId || !accessToken || !thread.conversation || thread.conversation.host_muted) return;
    const text = textareaRef.current?.value || '';
    if (!text.trim()) return;
    setSending(true);
    const optimistic = {
      id: `temp-${Date.now()}`,
      body: text,
      sender_type: 'host',
      created_at: new Date().toISOString(),
    };
    setThread((prev) => ({ ...prev, messages: [...(prev.messages || []), optimistic] }));
    textareaRef.current.value = '';
    try {
      const saved = await api.sendMessage(selectedId, text, accessToken);
      setThread((prev) => ({
        ...prev,
        messages: (prev.messages || []).map((m) => (m.id === optimistic.id ? saved : m)),
      }));
      await loadConversations(true);
    } catch (err) {
      setError(err.message || 'Failed to send message');
      setThread((prev) => ({
        ...prev,
        messages: (prev.messages || []).filter((m) => m.id !== optimistic.id),
      }));
    } finally {
      setSending(false);
    }
  };

  const onFlag = async () => {
    if (!selectedId) return;
    setFlagging(true);
    try {
      await api.flagConversation(selectedId, 'Host flagged conversation', accessToken);
      await loadConversations(true);
      await loadMessages(selectedId, true);
    } catch (err) {
      setError(err.message || 'Failed to flag conversation');
    } finally {
      setFlagging(false);
    }
  };

  const currentConversation = useMemo(
    () => conversations.find((c) => c.id === selectedId) || thread.conversation,
    [conversations, selectedId, thread.conversation]
  );

  const composerDisabled = thread.conversation?.host_muted;

  return (
    <HostSection title="Messages" description="Chat with guests about their bookings and stay details.">
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-3 lg:col-span-1">
          <div className="flex items-center gap-2 pb-2">
            {tabs.map((tab) => (
              <button
                key={tab.value || 'all'}
                type="button"
                onClick={() => setFilters((f) => ({ ...f, status: tab.value }))}
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  filters.status === tab.value ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="space-y-2">
            <input
              type="text"
              placeholder="Search guest or booking"
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
            <select
              value={filters.property_id}
              onChange={(e) => setFilters((f) => ({ ...f, property_id: e.target.value }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="">All properties</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          </div>
          <div className="mt-3 h-[600px] space-y-2 overflow-y-auto">
            {loadingList && <div className="text-sm text-slate-500">Loading conversations...</div>}
            {!loadingList && conversations.length === 0 && (
              <div className="text-sm text-slate-500">No conversations yet.</div>
            )}
            {conversations.map((c) => (
              <ConversationListItem
                key={c.id}
                convo={c}
                active={selectedId === c.id}
                onClick={() => setSelectedId(c.id)}
              />
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-4">
          {!selectedId && <div className="text-sm text-slate-500">Select a conversation to view messages.</div>}
          {selectedId && (
            <div className="flex h-full flex-col gap-3">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2">
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    {thread.contact?.guest_name || currentConversation?.guest_name || 'Guest'} |{' '}
                    {thread.booking?.booking_code || currentConversation?.booking_code}
                  </p>
                  <p className="text-xs text-slate-500">
                    {thread.booking?.property_title || currentConversation?.property_title || 'Property'} |{' '}
                    {thread.booking?.check_in_date} - {thread.booking?.check_out_date}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <QuickLink href={thread.contact?.quick_links?.email} label="Email" />
                  <QuickLink href={thread.contact?.quick_links?.phone} label="Call" />
                  <QuickLink href={thread.contact?.quick_links?.whatsapp} label="WhatsApp" />
                  <button
                    type="button"
                    onClick={onFlag}
                    disabled={flagging}
                    className="rounded-full border border-amber-300 px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-50"
                  >
                    {flagging ? 'Flagging...' : 'Flag to admin'}
                  </button>
                </div>
              </div>

              {error && <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
              {thread.conversation?.host_muted && (
                <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  You have been muted by an admin. You can still read messages but cannot send new ones.
                </div>
              )}

              <div className="flex-1 space-y-3 overflow-y-auto rounded-lg bg-slate-50 p-3">
                {loadingMessages && <div className="text-sm text-slate-500">Loading messages...</div>}
                {!loadingMessages && (thread.messages || []).length === 0 && (
                  <div className="text-sm text-slate-500">No messages yet.</div>
                )}
                {(thread.messages || []).map((m) => (
                  <MessageBubble key={m.id} msg={m} isOwn={m.sender_type === 'host'} />
                ))}
              </div>

              <div className="rounded-lg border border-slate-200 p-3 shadow-sm">
                <textarea
                  ref={textareaRef}
                  disabled={composerDisabled || sending}
                  className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                  rows={3}
                  placeholder={composerDisabled ? 'You are muted by admin.' : 'Type your message...'}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      onSend();
                    }
                  }}
                />
                <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                  <span>Press Enter to send, Shift + Enter for new line.</span>
                  <button
                    type="button"
                    onClick={onSend}
                    disabled={sending || composerDisabled}
                    className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {sending ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </HostSection>
  );
};

export default HostMessages;
